import React, { useState } from 'react';
import { Box, CircularProgress, Stack, TextField, alpha } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import IconHandler from 'components/common/icon/IconHandler';
import IconMi from 'components/common/icon/IconMi';
import { conciergeAPI } from 'api/concierge';
import useBundleTranslation from 'i18n';

interface IProps {
    questionId: string;
    question: string;
    options: string[];
    // Row 2022: one short line under each option (a glossary term's definition); none on every other card
    optionDetails?: string[];
    botIcon?: string;
    questionIndex: number;
    totalQuestions: number;
    onAnswered: (answer: string | null) => void;
}

export default function ClarificationQuestion({
    questionId,
    question,
    options,
    optionDetails,
    botIcon,
    questionIndex,
    totalQuestions,
    onAnswered,
}: IProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);

    const conciergeSettings = useSelector((state: any) => state.auth?.configuration?.conciergeSettings) ?? {};
    const iconProps = {
        type: botIcon !== undefined ? 'upload' : conciergeSettings.bot_icon_ind,
        value:
            botIcon !== undefined
                ? botIcon
                : conciergeSettings.bot_icon_ind === 'upload'
                  ? `data:image/png;base64,${conciergeSettings.bot_icon_data}`
                  : conciergeSettings.bot_icon_name,
    };
    const [customAnswer, setCustomAnswer] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async (answer: string | null) => {
        if (loading) return;
        setLoading(true);
        try {
            await conciergeAPI.answerClarificationQuestion(questionId, answer);
        } catch {
            // ignore — stream continues regardless
        } finally {
            setLoading(false);
            onAnswered(answer);
        }
    };

    const handleCustomSubmit = () => {
        const trimmed = customAnswer.trim();
        if (trimmed) {
            submit(trimmed);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleCustomSubmit();
        }
    };

    return (
        <Stack
            direction="row"
            sx={{
                borderTop: (theme: Theme) => `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                background: (theme: Theme) =>
                    `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0)} 16%)`,
                p: 2,
                pb: 1.5,
                gap: '12px',
                flexShrink: 0,
            }}
        >
            <Box
                sx={{
                    width: '24px',
                    height: '24px',
                    flexShrink: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'inline-flex',
                    color: 'primary.main',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        display: 'inline-flex',
                        borderRadius: '50%',
                        overflow: 'hidden',
                    }}
                >
                    <IconHandler icon={iconProps} sx={{ fontSize: '24px' }} />
                </Box>
            </Box>

            <Stack sx={{ minWidth: 0, width: '100%', gap: '12px' }}>
                <Box>
                    <Stack direction="row" alignItems="flex-start">
                        <Box>
                            <Box
                                sx={{
                                    fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                                    color: 'primary.main',
                                    fontWeight: 500,
                                }}
                            >
                                {t('clarification.to_clarify')}
                            </Box>
                            <Box sx={{ color: 'text.primary' }}>{question}</Box>
                        </Box>
                        <Box sx={{ pl: 2, color: 'text.secondary', whiteSpace: 'nowrap', mt: 1 }}>
                            {t('clarification.pager', { index: questionIndex, total: totalQuestions })}
                        </Box>
                    </Stack>
                </Box>

                <Stack sx={{ gap: '4px' }}>
                    {options.map((option, idx) => (
                        <Stack
                            key={idx}
                            direction="row"
                            alignItems="center"
                            spacing={0}
                            onClick={() => !loading && submit(option)}
                            sx={{
                                borderRadius: '4px',
                                border: (theme: Theme) => `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                                cursor: loading ? 'default' : 'pointer',
                                backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.04),
                                '&:hover': !loading
                                    ? {
                                          borderColor: 'primary.main',
                                          backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
                                      }
                                    : undefined,
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    width: '26px',
                                    pr: '1px',
                                    flexShrink: 0,
                                    alignSelf: 'stretch',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
                                    color: 'primary.main',
                                }}
                            >
                                {idx + 1}
                            </Box>
                            <Box sx={{ pl: 1, py: 0.5 }}>
                                <Box sx={{ color: 'primary.main' }}>{option}</Box>
                                {optionDetails?.[idx] ? (
                                    <Box
                                        sx={{
                                            fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {optionDetails[idx]}
                                    </Box>
                                ) : null}
                            </Box>
                        </Stack>
                    ))}

                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <TextField
                            value={customAnswer}
                            onChange={(e) => setCustomAnswer(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Something else"
                            disabled={loading}
                            fullWidth
                        />
                        {customAnswer.trim() ? (
                            <Box
                                onClick={() => !loading && handleCustomSubmit()}
                                sx={{
                                    flexShrink: 0,
                                    alignSelf: 'stretch',
                                    pl: 1,
                                    width: '24px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    color: 'primary.main',
                                    cursor: loading ? 'default' : 'pointer',
                                    opacity: loading ? 0.5 : 1,
                                    '&:hover': !loading ? { opacity: 0.8 } : undefined,
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={14} />
                                ) : (
                                    <IconMi icon="send" sx={{ fontSize: '16px' }} />
                                )}
                            </Box>
                        ) : (
                            <Box
                                onClick={() => !loading && submit(null)}
                                sx={{
                                    flexShrink: 0,
                                    alignSelf: 'stretch',
                                    minWidth: '51px',
                                    px: '12px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    color: (theme) => alpha(theme.palette.text.primary, 0.8),
                                    cursor: loading ? 'default' : 'pointer',
                                    userSelect: 'none',
                                    backgroundColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.04),
                                    border: (theme: Theme) => `1px solid ${alpha(theme.palette.text.primary, 0.16)}`,
                                    borderRadius: '3px',
                                    opacity: loading ? 0.5 : 1,
                                    '&:hover': !loading
                                        ? {
                                              backgroundColor: (theme: Theme) =>
                                                  alpha(theme.palette.text.primary, 0.08),
                                              borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.32),
                                          }
                                        : undefined,
                                }}
                            >
                                {loading ? <CircularProgress size={14} /> : 'Skip'}
                            </Box>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
}
